/**
 * MSR EVT-04 (migration 2026-10-12-530): an account (organization) CTA can be marked for ONE event activity.
 * The stored value is an events.type value; NULL means "any activity" (every CTA created before 530).
 *
 * How the event CTA is chosen from the account (server: _eib_event_cta_proposal, gun shows and range nights):
 *   - only a CTA at the event CTA's voucher amount ($20) is copied (migration 440);
 *   - a CTA marked for ANOTHER activity is never copied;
 *   - the amount is matched first across the venue AND the organization level, then (530, owner 2026-10-12):
 *     venue CTA marked for this activity, org-level CTA marked for it, venue "Any" CTA, org-level "Any" CTA;
 *     ties newest first. So a venue with no CTA at the amount falls back to the organization's (440 did not).
 *
 * The server CHECK (organization_ctas_activity_type_check) accepts every event type; the picker offers the ones a
 * store or promoter account CTA is realistically for, plus the CTA's current value if it is something else.
 */

export const CTA_ACTIVITY_ANY = "";

export const CTA_ACTIVITY_OPTIONS: ReadonlyArray<{
  value: string;
  label: string;
}> = [
  { value: "range_night", label: "Range night" },
  { value: "gun_show", label: "Gun show" },
  { value: "class", label: "Class" },
  { value: "table", label: "Table" },
  { value: "seminar", label: "Seminar / workshop" },
  { value: "member_check_in", label: "Member check-in" },
  { value: "lightning_deals", label: "Lightning deals" },
  { value: "conference", label: "Conference / expo" },
  { value: "speaking_engagement", label: "Speaking engagement" },
];

/**
 * Every value the server accepts, for validating what we send. MUST equal events_type_check AND
 * organization_ctas_activity_type_check (mig 530); a new event type needs all three (ctaActivityType.test.ts pins
 * this list to the EventType enum).
 */
export const CTA_ACTIVITY_SERVER_VALUES: ReadonlyArray<string> = [
  "class",
  "table",
  "gun_show",
  "seminar",
  "conference",
  "speaking_engagement",
  "networking_event",
  "member_check_in",
  "lightning_deals",
  "range_night",
  "show",
  "private_event",
  "community_event",
  "meeting",
  "training",
  "webinar",
  "call",
  "social",
  "other",
];

function titleCase(value: string): string {
  return value
    .split("_")
    .map((w) => (w ? w[0]!.toUpperCase() + w.slice(1) : w))
    .join(" ");
}

/** Label for a stored value; null/empty = "Any activity". */
export function ctaActivityLabel(value: string | null | undefined): string {
  if (value === null || value === undefined || value === "") {
    return "Any activity";
  }
  const known = CTA_ACTIVITY_OPTIONS.find((o) => o.value === value);
  return known ? known.label : titleCase(value);
}

/** The picker's options for a CTA currently holding `current` (kept even if not in the short list). */
export function ctaActivityOptionsFor(
  current: string | null | undefined,
): Array<{ value: string; label: string }> {
  const opts = [
    { value: CTA_ACTIVITY_ANY, label: "Any activity (default)" },
    ...CTA_ACTIVITY_OPTIONS,
  ];
  if (
    current &&
    !CTA_ACTIVITY_OPTIONS.some((o) => o.value === current) &&
    CTA_ACTIVITY_SERVER_VALUES.includes(current)
  ) {
    opts.push({ value: current, label: ctaActivityLabel(current) });
  }
  return opts;
}

/** Picker value -> column value. "" (any) and anything the server would refuse become NULL. */
export function ctaActivityToColumn(
  value: string | null | undefined,
): string | null {
  if (!value) {
    return null;
  }
  return CTA_ACTIVITY_SERVER_VALUES.includes(value) ? value : null;
}
